import React from "react";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import AccessTimeIcon from "@mui/icons-material/AccessTime";

export interface PageHeroProps {
  badge: string;
  badgeIcon?: React.ReactNode;
  title: string;
  subtitle: string;
  updatedAt?: string | null;
}

export const PageHero: React.FC<PageHeroProps> = ({
  badge,
  badgeIcon,
  title,
  subtitle,
  updatedAt,
}) => {
  const formattedDate = updatedAt
    ? new Date(updatedAt).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : null;

  return (
    <div className="public_page_hero">
      <div className="hero_badge">
        {badgeIcon}
        <span>{badge}</span>
      </div>
      <h1 className="hero_title">{title}</h1>
      <p className="hero_subtitle">{subtitle}</p>

      {formattedDate && (
        <div className="hero_meta">
          <span className="meta_item">
            <AccessTimeIcon />
            Last updated: {formattedDate}
          </span>
        </div>
      )}
    </div>
  );
};

export const LoadingState: React.FC<{ lines?: number }> = ({ lines = 6 }) => {
  return (
    <div className="public_loading_state" aria-busy="true" aria-label="Loading content">
      <div className="skeleton_bar heading" />
      {Array.from({ length: lines }).map((_, idx) => {
        const widths = ["w90", "w75", "w90", "w60", "w75", "w40"];
        const widthClass = widths[idx % widths.length];
        return <div key={idx} className={`skeleton_bar ${widthClass}`} />;
      })}
    </div>
  );
};

export interface ErrorStateProps {
  title?: string;
  message?: string;
  onRetry?: () => void;
}

export const ErrorState: React.FC<ErrorStateProps> = ({
  title = "Failed to load content",
  message = "We encountered a temporary issue while retrieving the latest content from our servers. Please try again.",
  onRetry,
}) => {
  return (
    <div className="public_error_state">
      <div className="error_icon">
        <ErrorOutlineIcon />
      </div>
      <h3>{title}</h3>
      <p>{message}</p>
      {onRetry && (
        <button className="btn_retry" onClick={onRetry}>
          <RefreshIcon sx={{ fontSize: 18 }} />
          <span>Try Again</span>
        </button>
      )}
    </div>
  );
};

export interface EmptyStateProps {
  title?: string;
  message?: string;
}

export const EmptyState: React.FC<EmptyStateProps> = ({
  title = "No Content Available",
  message = "This section does not have any content published yet. Please check back later.",
}) => {
  return (
    <div className="public_empty_state">
      <div className="empty_icon">
        <HelpOutlineIcon />
      </div>
      <h4>{title}</h4>
      <p>{message}</p>
    </div>
  );
};
